import { useQuery, useMutation } from '@vue/apollo-composable';
import type { Query, UserNotification } from '@/types/composition-functions';
import {
  MY_NOTIFICATIONS,
  MY_UNREAD_NOTIFICATION_COUNT,
  MARK_NOTIFICATION_READ,
  MARK_ALL_NOTIFICATIONS_READ,
} from '@/graphql/notifications';
import { useNotificationStore, type AppNotification } from '@/store/notificationStore';

export interface NotificationCentreOptions {
  /** Page size. The bell only needs the newest few; the centre pages properly. */
  first?: number;
  /** 1-based page, reactive so the centre's paginator can drive it. */
  page?: Ref<number>;
  /** Server-side unread filter — see MyNotificationsQuery::builder. */
  unreadOnly?: Ref<boolean>;
  /**
   * Push the fetched page into the Pinia store. The bell (one instance per
   * shell) does; the centre page does not, so browsing page 4 of history
   * doesn't rewrite the bell's feed under the user.
   */
  hydrateStore?: boolean;
}

/** Map a persisted `UserNotification` onto the store's shape. */
export function toAppNotification(n: UserNotification): AppNotification {
  return {
    id: String(n.id),
    title: n.title ?? undefined,
    message: n.message,
    type: n.type || 'system',
    read: !!n.read_at,
    created_at: n.created_at,
    metadata: (n.metadata ?? {}) as Record<string, unknown>,
    persisted: true,
  };
}

/**
 * Server-backed notifications (#224). Hydrates the bell from `myNotifications`,
 * keeps the badge on `myUnreadNotificationCount` (the paginated list can only
 * count its own page), and wires read-state to the mutations so it survives a
 * reload and matches on a second device.
 *
 * Read-state is applied optimistically and rolled back if the mutation fails,
 * mirroring useAnnouncements: the server stays the source of truth on the next
 * refetch, but the badge reacts instantly.
 */
export function useNotificationCentre(options: NotificationCentreOptions = {}) {
  const store = useNotificationStore();
  const {
    first = 20, page, unreadOnly, hydrateStore = false,
  } = options;

  const variables = computed(() => ({
    first,
    page: page?.value ?? 1,
    unread_only: unreadOnly?.value ?? false,
  }));

  const {
    result, loading, error, refetch,
  } = useQuery<Pick<Query, 'myNotifications'>>(
    MY_NOTIFICATIONS,
    variables,
    { fetchPolicy: 'cache-and-network' },
  );

  const {
    result: countResult,
    refetch: refetchCount,
  } = useQuery<{ myUnreadNotificationCount: number }>(
    MY_UNREAD_NOTIFICATION_COUNT,
    undefined,
    { fetchPolicy: 'cache-and-network' },
  );

  const notifications = computed<AppNotification[]>(
    () => (result.value?.myNotifications?.data ?? []).map(toAppNotification),
  );
  const paginatorInfo = computed(() => result.value?.myNotifications?.paginatorInfo);
  const unreadCount = computed(() => countResult.value?.myUnreadNotificationCount ?? 0);

  if (hydrateStore) {
    watch(notifications, (items) => store.hydrate(items), { immediate: true });
  }
  watch(unreadCount, (count) => {
    if (countResult.value) store.setServerUnreadCount(count);
  }, { immediate: true });

  const { mutate: markReadMutation } = useMutation<
    { markNotificationRead: boolean }, { id: string }
  >(MARK_NOTIFICATION_READ);
  const { mutate: markAllReadMutation } = useMutation<
    { markAllNotificationsRead: boolean }
  >(MARK_ALL_NOTIFICATIONS_READ);

  /**
   * Mark one notification read. Items the backend never persisted (group
   * fan-out still broadcasts with a null id) have no row to write, so they stay
   * local-only rather than sending the invented client id to the server.
   */
  const markRead = async(id: string) => {
    const target = store.notifications.find((n) => n.id === id);
    if (target?.read) return;

    store.markAsRead(id);
    if (target && target.persisted === false) return;

    try {
      await markReadMutation({ id });
      await refetchCount();
    } catch (e) {
      store.markAsUnread(id); // roll back so the badge stays honest
      throw e;
    }
  };

  const markAllRead = async() => {
    store.markAllAsRead();
    try {
      await markAllReadMutation();
      await Promise.all([refetch(), refetchCount()]);
    } catch (e) {
      await Promise.all([refetch(), refetchCount()]); // resync from the server
      throw e;
    }
  };

  const refresh = async() => {
    await Promise.all([refetch(), refetchCount()]);
  };

  return {
    notifications,
    paginatorInfo,
    unreadCount,
    loading,
    error,
    refetch,
    refresh,
    markRead,
    markAllRead,
  };
}
